import React, { useEffect, useState } from 'react';
import { collection, getDocs, doc, updateDoc, getDoc } from 'firebase/firestore';
import { userDb } from '../firebaseUsers';
import emailjs from 'emailjs-com';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [birthdaysToday, setBirthdaysToday] = useState([]);
  const [sendingEmails, setSendingEmails] = useState(false);

  useEffect(() => {
    
    const fetchUsersAndSendEmails = async () => {
      if (sendingEmails) {
        // Prevent re-entry while sending emails
        return;
      }
      setSendingEmails(true);

      try {
        const snapshot = await getDocs(collection(userDb, 'users'));
        const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setUsers(list);
        setLoading(false);

        await checkAndSendBirthdayEmails(list);
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        setSendingEmails(false);
      }
    };

    fetchUsersAndSendEmails();
  }, []);

  const stripTime = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const isSameMonthAndDay = (d1, d2) => {
    const date1 = stripTime(d1);
    const date2 = stripTime(d2);
    return date1.getDate() === date2.getDate() && date1.getMonth() === date2.getMonth();
  };

  const checkAndSendBirthdayEmails = async (userList) => {
    const today = new Date();
    const year = today.getFullYear();
    const todaysBirthdays = [];

    for (const user of userList) {
      if (!user.birthday?.toDate || typeof user.birthday.toDate !== 'function') continue;

      const birthday = user.birthday.toDate();

      // Defensive Firestore check before sending
      const userDocRef = doc(userDb, 'users', user.id);
      const snapshot = await getDoc(userDocRef);
      const currentData = snapshot.data();
      const lastSent = currentData?.lastBirthdayEmailSent?.toDate?.();
      const alreadySentThisYear = lastSent?.getFullYear() === year;

      if (
        isSameMonthAndDay(birthday, today) &&
        !alreadySentThisYear
      ) {
        // console.log(`Sending birthday email to: ${user.username} (${user.email})`);
        const success = await sendBirthdayEmail(user);
        if (success) {
          todaysBirthdays.push(user);
          // Update Firestore after sending email
          await updateDoc(userDocRef, {
            lastBirthdayEmailSent: new Date(),
          });
        }
      }
    }

    setBirthdaysToday(todaysBirthdays);
  };

  const sendBirthdayEmail = async (user) => {
    const { username, email } = user;

    const messages = [
      "Wishing you a day filled with joy, style, and celebration!",
      "May your birthday be as special and stylish as you are!",
      "With Wonder & Gratitude — Happy Birthday from all of us!",
      "Thanks for being part of the Wear & Wonder family. Have a wonderful birthday!"
    ];
    const message = messages[Math.floor(Math.random() * messages.length)];

    const html_body = `
      <div style="
        background: url('https://res.cloudinary.com/dcuturlmb/image/upload/v1759925253/my_uploads/hn4jqls37tzvfub1ou7x.jpg') center/cover no-repeat;
        font-family: Arial, sans-serif;
        padding: 40px;
        color: #333;
        text-align: center;
      ">
        <img src="https://res.cloudinary.com/dcuturlmb/image/upload/v1759925386/my_uploads/hxauczbx0yznuhyoumbz.png" style="height: 50px; margin-bottom: 20px;" />
        <h2 style="color: #D2691E;">🎉 Happy Birthday, ${username}!</h2>
        <p style="font-size: 18px;">${message}</p>

        <a href="https://res.cloudinary.com/dcuturlmb/image/upload/v1759925254/my_uploads/vtp0medpgtgympgfti2r.gif" target="_blank" style="
          display: inline-block;
          margin-top: 30px;
          padding: 12px 24px;
          background-color: #D2691E;
          color: white;
          font-size: 16px;
          border-radius: 5px;
          text-decoration: none;
        ">
          🎉 Surprise!
        </a>

        <p style="margin-top: 30px;">– Your friends at Wear & Wonder</p>
        <hr style="margin: 30px auto; width: 60%;" />
        <p style="color: #888; font-size: 14px;">You're receiving this email because you're a valued Wear & Wonder user.</p>
      </div>
    `;

    const emailData = {
      from_name: 'Wear & Wonder',
      to_name: username,
      to_email: email,
      html_body,
    };

    try {
      const response = await emailjs.send(
        'service_ra0tzqd',
        'template_04sjurs',
        emailData,
        'K6B2QrefJnYCDCTa1'
      );
      // console.log(`✅ Birthday email sent to ${username}`, response.status);
      return true;
    } catch (error) {
      console.error(`❌ Failed to send birthday email to ${username}`, error);
      return false;
    }
  };

  // Stats calculations
  const totalUsers = users.length;

  const genderCounts = users.reduce((acc, user) => {
    const gender = user.gender || 'Unspecified';
    acc[gender] = (acc[gender] || 0) + 1;
    return acc;
  }, {});

  const ageRangeCounts = users.reduce((acc, user) => {
    const range = user.ageRange || 'Unspecified';
    acc[range] = (acc[range] || 0) + 1;
    return acc;
  }, {});

  const formatPercentage = (count) =>
    totalUsers > 0 ? ((count / totalUsers) * 100).toFixed(1) + '%' : '0%';

  return (
    <div style={{ padding: '1rem' }}>
      <h2>👥 Admin Users</h2>

      {loading ? (
        <p>Loading users...</p>
      ) : (
        <>
          <h4>Total Users: {totalUsers}</h4>

          <div style={{ marginTop: '1rem' }}>
            <h3>Gender Distribution</h3>
            <ul>
              {Object.entries(genderCounts).map(([gender, count]) => (
                <li key={gender}>
                  {gender}: {count} ({formatPercentage(count)})
                </li>
              ))}
            </ul>
          </div>

          <div style={{ marginTop: '1rem' }}>
            <h3>Age Range Distribution</h3>
            <ul>
              {Object.entries(ageRangeCounts).map(([range, count]) => (
                <li key={range}>
                  {range}: {count} ({formatPercentage(count)})
                </li>
              ))}
            </ul>
          </div>

          {birthdaysToday.length > 0 && (
            <div
              style={{
                marginTop: '2rem',
                background: '#fffae6',
                padding: '1rem',
                borderRadius: '8px',
              }}
            >
              <h3>🎂 Birthdays Today</h3>
              <ul>
                {birthdaysToday.map((user) => (
                  <li key={user.id}>
                    {user.username} ({user.email})
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div style={{ marginTop: '2rem' }}>
            <h3>User List</h3>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
              }}
            >
              {users.map((user) => (
                <div
                  key={user.id}
                  style={{
                    padding: '1rem',
                    border: '1px solid #ccc',
                    borderRadius: 10,
                    background: '#f9f9f9',
                  }}
                >
                  <strong>{user.username}</strong> ({user.email})
                  <br />
                  Gender: {user.gender || 'N/A'}, Age Range: {user.ageRange || 'N/A'}
                  <br />
                  Birthday: {user.birthday?.toDate?.().toLocaleDateString() || 'N/A'}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminUsers;
